import EmulationTokenRecord from "models/view-models/emulation-token-record";

import useLocalStorage from "utilities/hooks/use-local-storage";
import { AuthenticationStrategyType } from "utilities/enumerations/authorization";
import { CookieUtils } from "utilities/cookie-utils";
import { EmulationModeContext } from "utilities/contexts/emulation/emulation-mode-context-provider";
import { RouteUtils } from "utilities/route-utils";
import { ToastManager } from "utilities/toast/toast-manager";
import { siteMap } from "internal-sitemap";
import { useAuthentication } from "utilities/contexts/authentication/authentication-provider";
import { useAuthenticationFactory } from "utilities/contexts/authentication/use-authentication.factory";
import { useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { LocalStorageUtils } from "utilities/local-storage-utils";
import { useGlobalState } from "../use-global-state-context";
import LocalStorageKey from "utilities/enumerations/local-storage-keys";
import EmulationToken from "models/interfaces/emulation-token";
import moment from "moment";

const UNABLE_TO_CREATE_TOKEN = "Unable to create emulation token.";
const ACCESS_TOKEN = "AccessToken";
export enum EmulationModeStatus {
    Resume,
    Restart,
    Clean,
}

export default function useEmulationMode() {
    const {
        token,
        setToken,
        endingEmulationSession,
        setLoadingEmulationSession,
        userEmail,
        endEmulationMode,
    } = useContext(EmulationModeContext);
    const { authenticationFactory } = useAuthenticationFactory();
    const { login } = useAuthentication();
    const history = useHistory();
    const { getValue } = useLocalStorage<EmulationTokenRecord | null>(
        LocalStorageKey.EmulationToken,
        null
    );
    const { globalState, setGlobalState } = useGlobalState();

    const createToken = async (guestId: number | undefined) => {
        if (guestId == null) {
            return ToastManager.error(UNABLE_TO_CREATE_TOKEN);
        }

        const accessToken = CookieUtils.get(ACCESS_TOKEN);

        if (!accessToken) {
            const authStrategyResult = await authenticationFactory(
                AuthenticationStrategyType.AzureB2C
            );

            await authStrategyResult.authStrategy.verifyLogin();
        }
        setLoadingEmulationSession(true);

        LocalStorageUtils.set(
            LocalStorageKey.EmulationOriginalUserLogin,
            globalState?.currentIdentity?.userLogin
        );

        await login(AuthenticationStrategyType.Emulation);

        history.push(RouteUtils.getUrl(siteMap.dashboards.user));
        setLoadingEmulationSession(false);
    };

    const createOrgMemberToken = async (
        guestId: number | undefined,
        userRoleOrganizationId: number | undefined
    ) => {
        if (guestId == null || userRoleOrganizationId == null) {
            return ToastManager.error(UNABLE_TO_CREATE_TOKEN);
        }
        setLoadingEmulationSession(true);
        LocalStorageUtils.set(
            LocalStorageKey.EmulationOriginalUserLogin,
            globalState?.currentIdentity?.userLogin
        );

        await login(AuthenticationStrategyType.Emulation);

        history.push(RouteUtils.getUrl(siteMap.dashboards.user));
        setLoadingEmulationSession(false);
    };

    const cleanEmulationMode = (inEmulationMode: boolean) => {
        LocalStorageUtils.remove(LocalStorageKey.EmulationToken);
        if (inEmulationMode)
            LocalStorageUtils.set<AuthenticationStrategyType>(
                LocalStorageKey.AuthStrategyType,
                AuthenticationStrategyType.AzureB2C
            );
    };

    const resumeEmulationMode = () => {
        const now = moment();
        //Were we in emulation mode:
        const inEmulationMode =
            LocalStorageUtils.get<AuthenticationStrategyType>(
                LocalStorageKey.AuthStrategyType
            ) === AuthenticationStrategyType.Emulation;
        if (inEmulationMode) {
            //is the token still valid?
            //we hit this when the token has not yet been issues, and so emulationToken is null
            const emulationToken = LocalStorageUtils.get<EmulationToken>(
                LocalStorageKey.EmulationToken
            );
            if (now.isAfter(emulationToken?.expiresOn)) {
                //EXPIRED!!!
                cleanEmulationMode(true);
                //The identity in globalState is likely incorrect.
                setGlobalState((prev) => prev.setUnauthenticated());
                return EmulationModeStatus.Restart;
            }
        } else {
            cleanEmulationMode(false);
            return EmulationModeStatus.Clean;
        }
        return EmulationModeStatus.Resume;
    };

    const isInEmulationMode = useMemo(() => {
        return getValue() != null || endingEmulationSession;
    }, [getValue, endingEmulationSession]);

    return {
        isInEmulationMode,
        token,
        setToken,
        userEmail,
        createToken,
        createOrgMemberToken,
        login: login,
        endEmulationMode,
        resumeEmulationMode,
    };
}
